'use client'

import { useState, ReactNode, useEffect, useCallback } from 'react'
import { DesktopNavigation } from './DesktopNavigation'
import { MobileNavigation } from './MobileNavigation'
import { LogoSection } from './LogoSection'
import { MobileMenuButton } from './MobileMenuButton'
import { SearchAndCommandPalette } from './SearchAndCommandPalette'
import { ContextualActions } from './ContextualActions'

/**
 * BIRHAUS FourThreeOneNav Component
 * 
 * Implements BIRHAUS principle #1: "Form serves flow" with 4-3-1 cognitive rule:
 * - Maximum 4 primary navigation items (Miller's Law: 7±2, optimized for 4)
 * - Maximum 3 contextual actions per view
 * - 1 primary action (clearly highlighted)
 * 
 * Features:
 * - Spanish-first internationalization
 * - Mobile-first responsive design
 * - Progressive disclosure for overflow items
 * - WCAG AA+ accessibility compliance
 * - Command palette integration (⌘K)
 */

// BIRHAUS: Utility function (replaces @/lib/utils cn)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

export interface NavigationItem {
  id: string
  label: string
  labelEn?: string // Optional English translation
  icon?: ReactNode
  href?: string
  onClick?: () => void
  active?: boolean
  disabled?: boolean
  badge?: string | number // Notification count/label
  submenu?: NavigationItem[]
}

export interface ContextualAction {
  id: string
  label: string
  labelEn?: string
  icon?: ReactNode
  onClick: () => void
  variant?: 'default' | 'secondary' | 'destructive'
  disabled?: boolean
  loading?: boolean
}

export interface PrimaryAction {
  label: string
  labelEn?: string
  icon?: ReactNode
  onClick: () => void
  disabled?: boolean
  loading?: boolean
}

export interface FourThreeOneNavProps {
  // BIRHAUS: Maximum 4 navigation items
  navigationItems: NavigationItem[]
  
  // BIRHAUS: Maximum 3 contextual actions  
  contextualActions?: ContextualAction[]
  
  // BIRHAUS: 1 primary action
  primaryAction?: PrimaryAction
  
  // Logo/Brand
  logo?: ReactNode
  logoHref?: string
  onLogoClick?: () => void
  
  // Search integration
  searchPlaceholder?: string
  onSearchClick?: () => void
  showSearch?: boolean
  
  // Command palette
  showCommandPalette?: boolean
  onCommandPaletteToggle?: () => void
  commandPaletteShortcut?: string
  
  // Mobile behavior
  mobileBreakpoint?: 'sm' | 'md' | 'lg'
  collapsible?: boolean
  
  // Spanish-first labels
  menuLabel?: string
  closeMenuLabel?: string
  searchLabel?: string
  commandPaletteLabel?: string
  
  // Styling
  className?: string
  variant?: 'default' | 'minimal' | 'elevated'
  
  // Overflow behavior (when > 4 nav items)
  overflowBehavior?: 'hide' | 'collapse' | 'scroll'
  overflowLabel?: string
}







// Helper component for actions section
function ActionsSection({
  showSearch,
  onSearchClick,
  searchLabel,
  searchPlaceholder,
  showCommandPalette,
  onCommandPaletteToggle,
  commandPaletteLabel,
  commandPaletteShortcut,
  contextualActions,
  primaryAction,
  bp
}: {
  showSearch: boolean
  onSearchClick?: () => void
  searchLabel: string
  searchPlaceholder: string
  showCommandPalette: boolean
  onCommandPaletteToggle?: () => void
  commandPaletteLabel: string
  commandPaletteShortcut: string
  contextualActions: ContextualAction[]
  primaryAction?: PrimaryAction
  bp: string
}) {
  return (
    <div className="flex items-center space-x-3">
      <SearchAndCommandPalette
        showSearch={showSearch}
        onSearchClick={onSearchClick}
        searchLabel={searchLabel}
        searchPlaceholder={searchPlaceholder}
        showCommandPalette={showCommandPalette}
        onCommandPaletteToggle={onCommandPaletteToggle}
        commandPaletteLabel={commandPaletteLabel}
        commandPaletteShortcut={commandPaletteShortcut}
        bp={bp}
      />

      <ContextualActions
        contextualActions={contextualActions}
        primaryAction={primaryAction}
        bp={bp}
      />
    </div>
  )
}

// eslint-disable-next-line max-lines-per-function -- Main navigation component requires comprehensive layout logic
export function FourThreeOneNav({
  navigationItems,
  contextualActions = [],
  primaryAction,
  logo,
  logoHref,
  onLogoClick,
  searchPlaceholder = 'Buscar...', // Used for aria-label and placeholder text
  onSearchClick,
  showSearch = true,
  showCommandPalette = true,
  onCommandPaletteToggle,
  commandPaletteShortcut = '⌘K',
  mobileBreakpoint = 'md',
  collapsible = true,
  menuLabel = 'Menú',
  closeMenuLabel = 'Cerrar menú',
  searchLabel = 'Buscar',
  commandPaletteLabel = 'Abrir paleta de comandos',
  className = '',
  variant = 'default',
  overflowBehavior = 'collapse',
  overflowLabel = 'Más opciones'
}: FourThreeOneNavProps) {
  
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null)
  
  // BIRHAUS: Enforce 4-3-1 rule with warnings
  useEffect(() => {
    if (navigationItems.length > 4) {
      console.warn(
        `BIRHAUS 4-3-1 Rule Violation: Navigation has ${navigationItems.length} items (max 4). ` +
        `Consider using overflowBehavior to handle excess items.`
      )
    }
    
    if (contextualActions.length > 3) {
      console.warn(
        `BIRHAUS 4-3-1 Rule Violation: ${contextualActions.length} contextual actions (max 3). ` +
        `Reduce cognitive load by consolidating actions.`
      )
    }
  }, [navigationItems.length, contextualActions.length])
  
  // BIRHAUS: Handle overflow navigation items
  const visibleNavItems = navigationItems.slice(0, 4)
  const overflowNavItems = navigationItems.slice(4)
  
  // BIRHAUS: Limit contextual actions to 3
  const visibleContextualActions = contextualActions.slice(0, 3)
  
  // Fix object injection by using explicit property access
  const getBreakpointClass = (breakpoint: typeof mobileBreakpoint): string => {
    switch (breakpoint) {
      case 'sm': return 'sm'
      case 'md': return 'md'
      case 'lg': return 'lg'
      default: return 'md'
    }
  }
  const bp = getBreakpointClass(mobileBreakpoint)
  
  // Fix object injection by using explicit property access
  const getNavVariantClass = (v: typeof variant): string => {
    switch (v) {
      case 'default': return 'bg-white border-b border-gray-200'
      case 'minimal': return 'bg-transparent'
      case 'elevated': return 'bg-white shadow-sm border-b border-gray-200'
      default: return 'bg-white border-b border-gray-200'
    }
  }
  
  const handleNavItemClick = (item: NavigationItem) => {
    if (item.disabled) return
    
    if (item.submenu && item.submenu.length > 0) {
      setActiveSubmenu(activeSubmenu === item.id ? null : item.id)
    } else {
      item.onClick?.()
      setIsMobileMenuOpen(false)
    }
  }
  
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Command palette shortcut
    if ((e.metaKey || e.ctrlKey) && e.key === 'k' && showCommandPalette) {
      e.preventDefault()
      onCommandPaletteToggle?.()
    }
  }, [showCommandPalette, onCommandPaletteToggle])

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])
  
  return (
    <nav 
      className={cn(
        'relative w-full',
        getNavVariantClass(variant),
        className
      )}
      data-birhaus-component="four-three-one-nav"
      aria-label="Navegación principal"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          
          {/* Logo/Brand */}
          <LogoSection
            logo={logo}
            logoHref={logoHref}
            onLogoClick={onLogoClick}
          />
          
          {/* BIRHAUS: Desktop Navigation (max 4 items) */}
          <DesktopNavigation
            visibleNavItems={visibleNavItems}
            overflowNavItems={overflowNavItems}
            activeSubmenu={activeSubmenu}
            onItemClick={handleNavItemClick}
            onSubmenuToggle={setActiveSubmenu}
            overflowBehavior={overflowBehavior}
            overflowLabel={overflowLabel}
            bp={bp}
          />
          
          {/* BIRHAUS: Actions Section (Search + 3 contextual + 1 primary) */}
          <div className="flex items-center space-x-3">
            <ActionsSection
              showSearch={showSearch}
              onSearchClick={onSearchClick}
              searchLabel={searchLabel}
              searchPlaceholder={searchPlaceholder}
              showCommandPalette={showCommandPalette}
              onCommandPaletteToggle={onCommandPaletteToggle}
              commandPaletteLabel={commandPaletteLabel}
              commandPaletteShortcut={commandPaletteShortcut}
              contextualActions={visibleContextualActions}
              primaryAction={primaryAction}
              bp={bp}
            />

            {/* Mobile menu button */}
            <MobileMenuButton
              isOpen={isMobileMenuOpen}
              onToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              collapsible={collapsible}
              menuLabel={menuLabel}
              closeMenuLabel={closeMenuLabel}
              bp={bp}
            />
          </div>
        </div>
        
        {/* BIRHAUS: Mobile Navigation */}
        {collapsible && isMobileMenuOpen && (
          <MobileNavigation
            navigationItems={navigationItems}
            contextualActions={visibleContextualActions}
            primaryAction={primaryAction}
            onItemClick={handleNavItemClick}
            onCloseMobileMenu={() => setIsMobileMenuOpen(false)}
            bp={bp}
          />
        )}
      </div>
    </nav>
  )
}

// BIRHAUS: Hook for managing navigation state
export function useFourThreeOneNav() {
  const [activeItem, setActiveItem] = useState<string | null>(null)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  
  const activateItem = (itemId: string) => {
    setActiveItem(itemId)
    setIsMobileMenuOpen(false)
  }
  
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(prev => !prev)
  }
  
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false)
  }
  
  return {
    activeItem,
    isMobileMenuOpen,
    activateItem,
    toggleMobileMenu,
    closeMobileMenu,
    setActiveItem
  }
}

// Export all components for external use
export { DesktopNavigation } from './DesktopNavigation'
export { MobileNavigation } from './MobileNavigation'
export { LogoSection } from './LogoSection'
export { MobileMenuButton } from './MobileMenuButton'